import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Loader,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { NotificationService } from '../../services/notificationService';
import { DEFAULT_NOTIFICATION_AI_PROMPT } from '../../data/notificationAiPrompt';

const COUNT_OPTIONS = [25, 50, 100];

function formatTime(value) {
  if (!value) return '—';
  const m = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!m) return String(value).slice(0, 5);
  const h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ampm} IST`;
}

const NotificationAiStudio = ({
  currentUser,
  categories,
  audienceSegments,
  targetRoleLabels,
  onSent,
}) => {
  const [audience, setAudience] = useState('customer');
  const [category, setCategory] = useState('reminder');
  const [count, setCount] = useState(100);
  const [brief, setBrief] = useState('');
  const [prompt, setPrompt] = useState(DEFAULT_NOTIFICATION_AI_PROMPT);
  const [promptDirty, setPromptDirty] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [campaignName, setCampaignName] = useState('Daily RoadServe ping');
  const [sendTime, setSendTime] = useState('10:00');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [result, setResult] = useState(null);
  const [audiencePreview, setAudiencePreview] = useState({ dbUsers: 0, appInstalls: 0 });

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const data = await NotificationService.listAiCampaigns();
      setCampaigns(data);
    } catch (e) {
      const msg = e?.message || '';
      if (msg.includes('does not exist') || msg.includes('Could not find') || e?.code === '42P01') {
        setResult({
          type: 'error',
          message: 'Pehle SQL run karo: supabase/notification-ai-campaigns.sql',
        });
      } else {
        console.error('load AI campaigns', e);
      }
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
    (async () => {
      try {
        const saved = await NotificationService.getAiPrompt();
        if (saved?.trim()) setPrompt(saved);
      } catch {
        // table not created yet — keep default prompt in the editor
      }
    })();
  }, [loadCampaigns]);

  useEffect(() => {
    let cancelled = false;
    NotificationService.getAudiencePreview(audience)
      .then((preview) => {
        if (!cancelled) setAudiencePreview(preview);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [audience]);

  const handleGenerate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const copies = await NotificationService.generateAiCopies({
        audience,
        count,
        brief,
        prompt,
      });
      setItems(copies);
      setResult({
        type: 'success',
        message: `${copies.length} Zomato-style RoadServe copies ready. Review karke scheduler save karo.`,
      });
    } catch (e) {
      setResult({ type: 'error', message: e.message || 'Generate fail hua' });
    } finally {
      setGenerating(false);
    }
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSavePrompt = async () => {
    if (!prompt.trim()) {
      setResult({ type: 'error', message: 'Prompt khali nahi ho sakta' });
      return;
    }
    setSavingPrompt(true);
    setResult(null);
    try {
      await NotificationService.saveAiPrompt(prompt, currentUser?.id || null);
      setPromptDirty(false);
      setResult({ type: 'success', message: 'Prompt save ho gaya. Agli generate isi se hogi.' });
    } catch (e) {
      setResult({
        type: 'error',
        message: e.message?.includes('does not exist')
          ? 'Pehle SQL run karo: supabase/notification-ai-prompt-settings.sql'
          : e.message || 'Prompt save nahi hua',
      });
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (items.length < 1) {
      setResult({ type: 'error', message: 'Pehle AI se copies generate karo' });
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      await NotificationService.saveAiCampaign({
        name: campaignName.trim() || 'Daily RoadServe ping',
        targetRole: audience,
        category,
        sendTimeIst: sendTime || '10:00',
        enabled: true,
        items,
        createdBy: currentUser?.id || null,
      });
      setResult({
        type: 'success',
        message: `Scheduler on — har din ${formatTime(sendTime)} pe next copy ${targetRoleLabels[audience] || audience} ko jayegi.`,
      });
      loadCampaigns();
    } catch (e) {
      setResult({
        type: 'error',
        message: e.message || 'Campaign save nahi hua. SQL run kiya kya?',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (campaign) => {
    try {
      await NotificationService.setAiCampaignEnabled(campaign.id, !campaign.enabled);
      loadCampaigns();
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ye AI campaign delete karein?')) return;
    try {
      await NotificationService.deleteAiCampaign(id);
      loadCampaigns();
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    }
  };

  const handleSendNext = async (campaign) => {
    setRunningId(campaign.id);
    setResult(null);
    try {
      const json = await NotificationService.runAiCampaignNow(campaign.id);
      const row = json?.results?.[0];
      if (row?.skipped) {
        setResult({ type: 'error', message: row.reason || 'Send skip ho gaya' });
      } else {
        setResult({
          type: 'success',
          message: `Abhi bhej di: “${row?.title || 'notification'}” — ${row?.sent || 0} app installs, phone pe ${row?.push_ok ?? 0} deliver`,
        });
        onSent?.();
        loadCampaigns();
      }
    } catch (e) {
      setResult({ type: 'error', message: e.message });
    } finally {
      setRunningId(null);
    }
  };

  const selectedAudience = audienceSegments.find((s) => s.id === audience);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
      <div className="xl:col-span-3 space-y-5">
        <div className="bg-white rounded-[1.75rem] border border-stone-200/80 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-stone-100 bg-slate-950 text-white">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Sparkles size={18} className="text-teal-300" />
              AI copies
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Zomato energy, RoadServe brand — highway, battery, tyre, membership.
            </p>
          </div>

          <div className="p-6 space-y-5">
            {result && (
              <div
                className={`p-4 rounded-lg flex items-start gap-3 ${
                  result.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {result.type === 'success' ? (
                  <CheckCircle size={20} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle size={20} className="mt-0.5 shrink-0" />
                )}
                <span className="font-medium text-sm">{result.message}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {audienceSegments.map((segment) => {
                  const Icon = segment.icon;
                  const active = audience === segment.id;
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      onClick={() => setAudience(segment.id)}
                      className={`text-left rounded-xl border-2 p-3 transition ${
                        active ? 'shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      style={
                        active
                          ? { borderColor: segment.accent, backgroundColor: segment.soft }
                          : undefined
                      }
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                        style={{
                          backgroundColor: active ? segment.accent : '#f3f4f6',
                          color: active ? '#fff' : '#6b7280',
                        }}
                      >
                        <Icon size={16} />
                      </div>
                      <p
                        className="font-semibold text-sm"
                        style={{ color: active ? segment.accent : '#111827' }}
                      >
                        {segment.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2.5 text-sm text-blue-900">
              <span className="font-semibold">{audiencePreview.appInstalls}</span> app installs
              <span className="text-blue-700/80"> (daily push inhe jayegi)</span>
              {audiencePreview.dbUsers > audiencePreview.appInstalls ? (
                <span className="text-blue-700/80">
                  {' '}
                  · {audiencePreview.dbUsers} DB records app nahi hain
                </span>
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kitni copies</label>
                <div className="flex gap-2">
                  {COUNT_OPTIONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setCount(n)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                        count === n
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  AI prompt
                  {promptDirty && (
                    <span className="ml-2 text-xs font-normal text-amber-600">unsaved</span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setPrompt(DEFAULT_NOTIFICATION_AI_PROMPT);
                    setPromptDirty(true);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
                >
                  <RotateCcw size={12} />
                  Reset default
                </button>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value);
                  setPromptDirty(true);
                }}
                rows={12}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 font-mono text-xs leading-relaxed resize-y"
                placeholder="Yahan prompt likho — tone, offers, kya bolna hai / kya nahi"
              />
              <p className="text-xs text-gray-400 mt-1">
                Ye prompt save hota hai. Generate isi text se copies banata hai.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extra brief <span className="text-gray-400 font-normal">(is baar ke liye, optional)</span>
              </label>
              <input
                type="text"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                placeholder="e.g. monsoon highway, ₹99 monthly, night breakdown"
                maxLength={400}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleSavePrompt}
                disabled={savingPrompt || !prompt.trim()}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingPrompt ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                Save prompt
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 px-5 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium shadow-sm"
              >
                {generating ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    <span>Copies ban rahe hain… ~20 sec</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>Generate {count} copies</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {items.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{items.length} copies</h3>
                <p className="text-xs text-gray-500">Edit / delete, phir scheduler save karo</p>
              </div>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-100">
              {items.map((item, index) => (
                <div key={`${index}-${item.title.slice(0, 12)}`} className="p-4 hover:bg-gray-50/80">
                  <div className="flex items-start gap-3">
                    <span className="text-xs font-mono text-gray-400 w-7 pt-2.5">{index + 1}</span>
                    <div className="flex-1 space-y-2">
                      <input
                        value={item.title}
                        onChange={(e) => updateItem(index, 'title', e.target.value)}
                        className="w-full text-sm font-semibold text-gray-900 border border-transparent hover:border-gray-200 focus:border-violet-400 rounded px-2 py-1"
                        maxLength={60}
                      />
                      <textarea
                        value={item.message}
                        onChange={(e) => updateItem(index, 'message', e.target.value)}
                        rows={2}
                        className="w-full text-sm text-gray-600 border border-transparent hover:border-gray-200 focus:border-violet-400 rounded px-2 py-1 resize-none"
                        maxLength={180}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="xl:col-span-2 space-y-5">
        <div className="bg-white rounded-[1.75rem] border border-stone-200/80 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={18} className="text-teal-700" />
            <h2 className="text-base font-semibold text-slate-900">Daily scheduler</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Har din ek copy — queue 1, 2, 3… {items.length || 100} din unique, phir loop.
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Campaign name</label>
              <input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Daily RoadServe ping"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Time (IST)</label>
              <input
                type="time"
                value={sendTime}
                onChange={(e) => setSendTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <p className="text-xs text-gray-500">
              Sending to{' '}
              <span className="font-semibold" style={{ color: selectedAudience?.accent }}>
                {selectedAudience?.name}
              </span>{' '}
              · {audiencePreview.appInstalls} app installs · {items.length} in queue
            </p>
            <button
              type="button"
              onClick={handleSaveCampaign}
              disabled={saving || items.length === 0}
              className="w-full px-4 py-2.5 bg-teal-700 text-white rounded-xl hover:bg-teal-800 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader size={16} className="animate-spin" /> : <Clock size={16} />}
              Save & turn on daily
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">Saved campaigns</h2>
            <button
              type="button"
              onClick={loadCampaigns}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {loadingCampaigns ? (
            <div className="flex justify-center py-8">
              <Loader size={20} className="animate-spin text-violet-500" />
            </div>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              Abhi koi scheduler nahi. Generate karke save karo.
            </p>
          ) : (
            <div className="space-y-3">
              {campaigns.map((campaign) => {
                const queueLen = Array.isArray(campaign.items) ? campaign.items.length : 0;
                const next = campaign.items?.[campaign.next_index] || campaign.items?.[0];
                return (
                  <div key={campaign.id} className="rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatTime(campaign.send_time_ist)} ·{' '}
                          {targetRoleLabels[campaign.target_role] || campaign.target_role} ·{' '}
                          {campaign.next_index + 1}/{queueLen || 0}
                        </p>
                      </div>
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                          campaign.enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {campaign.enabled ? 'On' : 'Paused'}
                      </span>
                    </div>
                    {next?.title && (
                      <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                        Next: {next.title}
                      </p>
                    )}
                    {campaign.last_sent_at && (
                      <p className="text-[11px] text-gray-400 mt-1">
                        Last: {campaign.last_title || 'sent'}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => handleToggle(campaign)}
                        className="px-2.5 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-1"
                      >
                        {campaign.enabled ? <Pause size={12} /> : <Play size={12} />}
                        {campaign.enabled ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendNext(campaign)}
                        disabled={runningId === campaign.id}
                        className="px-2.5 py-1 text-xs rounded border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1 disabled:opacity-50"
                      >
                        {runningId === campaign.id ? (
                          <Loader size={12} className="animate-spin" />
                        ) : (
                          <Send size={12} />
                        )}
                        Send next now
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(campaign.id)}
                        className="ml-auto p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationAiStudio;
